// TODO(ui-foundation): promote this component to ui-foundation and remove from this repo.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type {
  MfaDeliveryDetails,
  LoginInitiateResponse,
} from "@its-engineering-and-development/jwt-auth-client";
import { eventsAdminUserServiceClient } from "@app/api/user-service-client";
import { AuthLinkButton } from "@app/components/atoms/auth-link-button";
import { AuthHeading } from "@app/components/molecules/auth-heading";
import { formatOneworld } from "@app/components/oneworld-word";
import { MfaChallengeForm } from "@app/components/organisms/mfa-challenge-form";
import { Spinner } from "@app/components/spinner";
import { AUTH_STATUS } from "@app/lib/auth-constants";
import {
  isRecoverableLoginMessage,
  isUnconfirmedUserAuthError,
  mapAuthError,
} from "@app/lib/auth-errors";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import {
  Button,
  ValidatedForm,
  ValidatedInput,
  createFormSchema,
  schemas,
  useValidatedForm,
} from "@app/lib/ui-foundation";

const loginSchema = createFormSchema({
  email: schemas.email,
});

type Stage = "credentials" | "mfa";

type MfaChallenge = {
  mfaTicket: string;
  deliveryDetails?: MfaDeliveryDetails;
};

type LoginInitiateChallengeResponse = Extract<
  LoginInitiateResponse,
  { mfaTicket: string }
>;

function hasMfaTicket(
  payload: LoginInitiateResponse,
): payload is LoginInitiateChallengeResponse {
  const maybePayload = payload as unknown as { mfaTicket?: unknown };
  return (
    typeof payload === "object" &&
    payload !== null &&
    "mfaTicket" in payload &&
    typeof maybePayload.mfaTicket === "string"
  );
}

function toMfaChallenge(payload: LoginInitiateChallengeResponse): MfaChallenge {
  return {
    mfaTicket: payload.mfaTicket,
    deliveryDetails: payload.deliveryDetails,
  };
}

export type LoginFormProps = {
  onSuccess: () => void;
  onSwitchToSignup?: () => void;
  onVerificationRequired?: (args: {
    email: string;
    statusMessage?: string;
  }) => void;
  initialEmail?: string;
  headingAs?: "h1" | "h2";
};

const RESEND_SUCCESS_MESSAGE = "We sent a new code to your email.";

export function LoginForm({
  onSuccess,
  onSwitchToSignup,
  onVerificationRequired,
  initialEmail = "",
  headingAs = "h2",
}: LoginFormProps) {
  const navigate = useNavigate();
  const { status, isAuthenticated, login } = useJwtAuth();

  const [stage, setStage] = useState<Stage>("credentials");
  const [cachedEmail, setCachedEmail] = useState(initialEmail);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [restartReason, setRestartReason] = useState("");

  const loginForm = useValidatedForm(loginSchema, {
    mode: "onChange",
    defaultValues: {
      // defaultValues is only consumed on mount; later updates to cachedEmail
      // flow through the explicit resetForm({ email: cachedEmail }) call.
      email: initialEmail,
    },
  });

  // Keep onSuccess in a ref so the auth effect below only re-fires when
  // isAuthenticated actually flips, regardless of whether the caller memoized
  // the callback.
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (isAuthenticated) {
      onSuccessRef.current();
    }
  }, [isAuthenticated]);

  const resetForm = loginForm.reset;

  const resetCredentialsStage = useCallback(
    (reason: string) => {
      setMfaChallenge(null);
      setStage("credentials");
      setErrorMessage("");
      setRestartReason(reason);
      resetForm({ email: cachedEmail });
    },
    [cachedEmail, resetForm],
  );

  const handleMfaRestart = useCallback(
    (reason: string) => resetCredentialsStage(reason),
    [resetCredentialsStage],
  );

  const handleBackToCredentials = useCallback(
    () => resetCredentialsStage(""),
    [resetCredentialsStage],
  );

  const navigateToVerification = useCallback(
    (args: { email: string; statusMessage?: string }) => {
      if (onVerificationRequired) {
        onVerificationRequired(args);
        return;
      }

      navigate("/verify-email", { state: args });
    },
    [navigate, onVerificationRequired],
  );

  const resumeVerification = useCallback(
    async (email: string, navigateOnFailure: boolean): Promise<boolean> => {
      try {
        await eventsAdminUserServiceClient.resendConfirmation({ email });
        navigateToVerification({
          email,
          statusMessage: RESEND_SUCCESS_MESSAGE,
        });
        return true;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[login] resend confirmation failed", error);
        }
        if (navigateOnFailure) {
          navigateToVerification({ email });
          return true;
        }
        return false;
      }
    },
    [navigateToVerification],
  );

  const handleCredentialsSubmit = useCallback(
    async (values: { email: string }) => {
      setErrorMessage("");
      setRestartReason("");
      try {
        const result = await login(values.email);
        if (hasMfaTicket(result)) {
          setCachedEmail(values.email);
          setMfaChallenge(toMfaChallenge(result));
          setStage("mfa");
        } else if (typeof result.message === "string" && result.message) {
          if (isRecoverableLoginMessage(result.message)) {
            const recovered = await resumeVerification(values.email, false);
            if (recovered) {
              return;
            }
          }
          setRestartReason(result.message);
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[login] credentials submit failed", error);
        }
        if (isUnconfirmedUserAuthError(error)) {
          const recovered = await resumeVerification(values.email, true);
          if (recovered) {
            return;
          }
        }
        setErrorMessage(
          mapAuthError(
            error,
            "Unable to send verification code. Please try again.",
          ),
        );
      }
    },
    [login, resumeVerification],
  );

  if (stage === "mfa" && mfaChallenge) {
    return (
      <MfaChallengeForm
        mfaTicket={mfaChallenge.mfaTicket}
        deliveryDetails={mfaChallenge.deliveryDetails}
        onRestartRequired={handleMfaRestart}
        onBack={handleBackToCredentials}
        headingAs={headingAs}
      />
    );
  }

  const isLoggingIn = loginForm.isSubmitting || status === AUTH_STATUS.LOADING;

  return (
    <>
      <AuthHeading
        as={headingAs}
        title="Login to your account"
        subtitle={formatOneworld("Manage your oneworld events effortlessly.")}
      />

      <p className="events-auth-card__notice" role="status" aria-live="polite">
        {restartReason}
      </p>

      <ValidatedForm
        form={loginForm}
        onSubmit={handleCredentialsSubmit}
        className="events-form-stack"
      >
        <ValidatedInput
          form={loginForm}
          name="email"
          label="Email Address"
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
        />
        <p className="events-auth-card__error" role="alert" aria-live="polite">
          {errorMessage}
        </p>

        <Button
          size="lg"
          color="primary"
          className="events-solid-button"
          type="submit"
          isDisabled={!loginForm.isValid || isLoggingIn}
        >
          <span className="events-login-button__content">
            Continue
            {isLoggingIn ? (
              <Spinner
                size={18}
                strokeWidth={2.5}
                color="#ffffff"
                trackColor="rgba(255, 255, 255, 0.35)"
                label="Sending code"
              />
            ) : null}
          </span>
        </Button>
      </ValidatedForm>

      {onSwitchToSignup ? (
        <div className="events-auth-card__footer">
          Don&apos;t have an account?{" "}
          <AuthLinkButton onClick={onSwitchToSignup}>Sign up</AuthLinkButton>
        </div>
      ) : null}
    </>
  );
}
